import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import NotificationService from '../services/NotificationService';

const NotificationContext = createContext();

export const useNotifications = () => {
  return useContext(NotificationContext);
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const auth = useAuth();
  const currentUser = auth ? auth.currentUser : null;
  const [isInitialized, setIsInitialized] = useState(false);

  // Load notifications from Firebase when user logs in
  useEffect(() => {
    let unsubscribe = null;
    
    if (currentUser) {
      console.log("Loading notifications for user:", currentUser.uid);
      
      unsubscribe = NotificationService.getUserNotifications(
        currentUser.uid,
        (notificationsList) => {
          console.log("Received notifications:", notificationsList);
          setNotifications(notificationsList);
        }
      );
      
      // Add a test notification if none exist (only once)
      if (!isInitialized) {
        setTimeout(() => {
          NotificationService.sendNotification(currentUser.uid, {
            message: "Welcome to Student Helper! Your notifications are working.",
            type: "info"
          });
          setIsInitialized(true);
        }, 2000);
      }
    } else {
      setNotifications([]);
    }
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser, isInitialized]);

  // Update unread count whenever notifications change
  useEffect(() => {
    const count = notifications.filter(notif => !notif.read).length;
    setUnreadCount(count);
  }, [notifications]);

  const addNotification = async (notification) => {
    if (!currentUser) return;
    
    try {
      console.log("Adding notification:", notification);
      
      // If userId is provided, send to that user
      if (notification.userId) {
        await NotificationService.sendNotification(notification.userId, notification);
      } 
      // If broadcast is true, send to all users
      else if (notification.broadcast) {
        await NotificationService.broadcastNotification(notification);
      } 
      // Otherwise, send to current user
      else {
        await NotificationService.sendNotification(currentUser.uid, notification);
      }
      
      console.log("Notification added successfully");
    } catch (error) {
      console.error("Error adding notification:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await NotificationService.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    if (!currentUser) return;
    
    try {
      await NotificationService.markAllAsRead(currentUser.uid);
      
      // Update local state
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, read: true }))
      );
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const clearNotifications = () => {
    // This just clears the UI, not the database
    setNotifications([]);
  };

  // For testing: create a test notification
  const createTestNotification = () => {
    if (!currentUser) return;
    
    addNotification({
      message: "This is a test notification " + new Date().toLocaleTimeString(),
      type: Math.random() > 0.5 ? "success" : "info"
    });
  };

  const value = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    createTestNotification // Add test function
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext; 